// services/distributionChartService.ts

import axios from 'axios';
import { API_URL } from '../config/config';
import { FilterParams } from '../views/leads/leadsGrid/leads.types';
import { getHeaderOptions } from "../services/getHeaderOptions";
import logger from '../utils/logger';

interface ChartDataParams {
    groupBy: string;
    filterPrams: Partial<FilterParams>;
}

export interface ChartDataPoint {
    _id: string;
    count: number;
    date: string;
}

export interface LocationPoint {
  _id: string;
  name?: string;
  count: number;
  latitude: number;
  longitude: number;
  address?: string;
}

interface ChartResponse {
    data: ChartDataPoint[];
    status: boolean;
    statusCode: number;
}

interface ApiLocationDataPoint {
  _id: string;
  name: string;
  address: {
    coords: {
      latitude: string | number;
      longitude: string | number;
      latitudeDelta: string | number;
      longitudeDelta: string | number;
    };
    address: string;
  };
}

interface LocationResponse {
    data: LocationPoint[];
    status: boolean;
    statusCode: number;
}

interface ApiLocationResponse {
    data: ApiLocationDataPoint[];
    status: boolean;
    statusCode: number;
}

const transformGridFiltersToChartParams = (gridFilters: FilterParams, groupBy: string): ChartDataParams => {
    const chartFilters = { ...gridFilters };
    
    // Remove only pagination and sorting, preserve list and other filters
    delete chartFilters.paginationParams;
    delete chartFilters.sort;
  
    // If no date range specified, default to last 7 days
    if (!chartFilters.date) {
      const end = new Date();
      const start = new Date();
      start.setDate(start.getDate() - 7);
      
      chartFilters.date = {
        startedAt: start.toISOString(),
        endedAt: end.toISOString()
      };
    }
  
    return {
      groupBy,
      filterPrams: chartFilters  // Now includes the list parameter
    };
};

const fetchDistributionData = async (filters: FilterParams, groupBy: string = 'createdAt'): Promise<ChartResponse> => {
    try {
        const params = transformGridFiltersToChartParams(filters, groupBy);
        logger.log('Sending params to API:', params); // Debug log
        
        const headers = {
            ...getHeaderOptions,
            Authorization: localStorage.getItem("auth_token") || "",
        };
        
        const response = await axios.post(
            `${API_URL}api/v1/dashboard/lead-grouped-count`, 
            params,
            { headers }
        );
        
        return response.data;
    } catch (error) {
        console.error('Error fetching distribution data:', error);
        throw error;
    }
};

/**
 * Fetches location data for map visualization
 * @param filters FilterParams from the grid
 * @param fieldName The name of the location field to visualize
 */
const fetchLocationData = async (filters: FilterParams, fieldName: string): Promise<LocationResponse> => {
    try {
        // Transform filters to required format
        const params = transformGridFiltersToChartParams(filters, "address");
        
        // Add the fieldName parameter - using the same endpoint
        const requestData = {
            ...params,
            fieldName
        };
        
        logger.log('Sending location params to API:', requestData);
        
        const headers = {
            ...getHeaderOptions,
            Authorization: localStorage.getItem("auth_token") || "",
        };
        
        // Use the same endpoint as the distribution chart
        const response = await axios.post(
            `${API_URL}api/v1/dashboard/lead-grouped-count`,
            requestData,
            { headers }
        );
        
        // Transform the API response to the expected LocationPoint format
        const transformedData: LocationPoint[] = response.data.data
            .filter((item: ApiLocationDataPoint) => {
                // Filter out items with empty coordinates
                const lat = parseFloat(String(item.address?.coords?.latitude));
                const lng = parseFloat(String(item.address?.coords?.longitude));
                return !isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0;
            })
            .map((item: ApiLocationDataPoint) => {
                // Convert each item to LocationPoint format
                return {
                    _id: item._id,
                    name: item.name,
                    count: 1, // Default count for each location
                    latitude: parseFloat(String(item.address?.coords?.latitude)),
                    longitude: parseFloat(String(item.address?.coords?.longitude)),
                    address: item.address?.address
                };
            });
        
        // Group by coordinates and sum counts
        const locationMap = new Map<string, LocationPoint>();
        
        transformedData.forEach(point => {
            const key = `${point.latitude},${point.longitude}`;
            
            if (locationMap.has(key)) {
                const existing = locationMap.get(key)!;
                existing.count += point.count;
                
                // Append names if they differ (optional)
                if (point.name && existing.name !== point.name) {
                    existing.name = `${existing.name}, ${point.name}`;
                }
            } else {
                locationMap.set(key, { ...point });
            }
        });
        
        const finalData = Array.from(locationMap.values());
        logger.log(`Transformed ${response.data.data.length} locations to ${finalData.length} map points`);
        
        return {
            data: finalData,
            status: response.data.status,
            statusCode: response.data.statusCode
        };
    } catch (error) {
        console.error('Error fetching location data:', error);
        throw error;
    }
};

export const distributionChartService = {
    fetchDistributionData,
    fetchLocationData
};